/**
 * Pure builders for the public `GET /api/forecast.json` export.
 *
 * Numbers come from the caller's already-fetched ensemble, wind, and tide
 * samples. This module does not call upstreams. Missing inputs stay null —
 * nothing here substitutes a default swell, wind, or tide.
 *
 * Verdict reuses `rateSurfQuality` (the same rater as the dashboard). A
 * best-window verdict is the mean of that rater's stars across the window:
 *   mean >= 3 (Fair+ / Good / Epic) → GO
 *   mean >= 2 (Fair)                 → FAIR
 *   otherwise (Poor / Flat)          → POOR
 * Renaming or removing response fields requires bumping `api_version`.
 */

// `.js` specifiers are required. Vercel emits this file as ESM and does not
// bundle it, and Node will not resolve an extensionless relative import.
import { localTimeToUtcMs } from "../../api/_shared/marine-ensemble.js";
import { RATING_CONFIG, rateSurfQuality, type SurfRating } from "./conditions.js";
import { selectDisplayedSwell } from "./displayedSwell.js";
import { estimateSurfFaceHeight } from "./marine.js";
import type { BreakType, CameraStream, RegionConfig, SurfBreak } from "./regions";
import { convertHeight } from "./units.js";
import type { Zone } from "./zones";

export const PUBLIC_API_VERSION = 1 as const;

export const FORECAST_JSON_CACHE_CONTROL =
  "public, s-maxage=900, max-age=900, stale-while-revalidate=900";

/**
 * Served snapshot. The origin read is one Firestore document. The CDN
 * holds it for a minute so a cron write shows up without a marine fetch.
 */
export const FORECAST_JSON_SNAPSHOT_CACHE_CONTROL =
  "public, s-maxage=60, max-age=60, stale-while-revalidate=300";

/**
 * Used when the refresh budget expires before every break is fetched.
 * Short enough that the next request can fill the nulls, long enough to
 * avoid a stampede. No `no-transform`, so the CDN can still compress.
 */
export const FORECAST_JSON_PARTIAL_CACHE_CONTROL =
  "public, s-maxage=60, max-age=60, stale-while-revalidate=60";

/** Three consecutive daylight hours. Shorter runs are not emitted. */
export const BEST_WINDOW_HOURS = 3;
/** How far ahead of `now` a window may start. */
export const BEST_WINDOW_HORIZON_MS = 36 * 60 * 60 * 1000;
/** Local hours included in a window, inclusive. */
export const DAYLIGHT_START_HOUR = 6;
export const DAYLIGHT_END_HOUR = 18;
/** Mean stars at or above this map to GO. Matches Fair+ (3 stars). */
export const VERDICT_GO_MIN_STARS = 3;
/** Mean stars at or above this, and below GO, map to FAIR. */
export const VERDICT_FAIR_MIN_STARS = 2;

const KMH_TO_MPH = 0.621371;
const COMPASS_8 = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"] as const;

export type PublicVerdict = "GO" | "FAIR" | "POOR";
export type TideTrendLabel = "rising" | "dropping" | "slack";

export interface PublicForecastFields {
  swell_ft: number | null;
  swell_period_s: number | null;
  swell_direction: string | null;
  wind_mph: number | null;
  wind_direction: string | null;
  gust_mph: number | null;
  tide_ft: number | null;
  tide_trend: TideTrendLabel | null;
  water_temp_f: number | null;
}

export interface PublicBestWindow {
  start: string;
  end: string;
  timezone: string;
  verdict: PublicVerdict;
  reason: string;
}

export interface PublicCrowd {
  reports_last_2h: number | null;
  thumbs_up: number | null;
  thumbs_down: number | null;
}

export interface PublicSpot {
  id: string;
  name: string;
  region: string;
  lat: number;
  lon: number;
  cam_url: string | null;
  forecast: PublicForecastFields;
  best_window: PublicBestWindow | null;
  crowd: PublicCrowd;
}

export interface PublicForecastResponse {
  api_version: typeof PUBLIC_API_VERSION;
  generated_at: string;
  spots: PublicSpot[];
}

export interface CatalogSpot {
  id: string;
  regionId: string;
  breakId: string;
  name: string;
  region: string;
  lat: number;
  lon: number;
  shoreNormal: number | null;
  breakType: BreakType | null;
  timezone: string | null;
  cam_url: string | null;
}

/** One hour, or the "current" sample when `time` is null. */
export interface HourConditions {
  /** Spot-local `YYYY-MM-DDTHH:mm` from Open-Meteo `timezone=auto`. */
  time: string | null;
  waveHeightM: number | null;
  wavePeriodS: number | null;
  waveDirectionDeg: number | null;
  swellHeightM: number | null;
  swellPeriodS: number | null;
  swellDirectionDeg: number | null;
  windSpeedKmh: number | null;
  windDirectionDeg: number | null;
  windGustKmh: number | null;
  waterTempC: number | null;
  tideHeightM: number | null;
  tideTrend: TideTrendLabel | null;
}

export interface SpotConditions {
  utcOffsetSeconds: number;
  /** IANA name from the region, or from the upstream `timezone=auto` response. */
  timezone: string | null;
  current: HourConditions;
  hourly: HourConditions[];
}

export function toUtcStamp(now: Date): string {
  return now.toISOString().replace(/\.\d{3}Z$/, "Z");
}

export function slugifyRegionId(regionId: string): string {
  const slug = regionId
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return slug || "region";
}

/** Stable public id: slug(region id) + the break's existing id. */
export function publicSpotId(regionId: string, breakId: string): string {
  return `${slugifyRegionId(regionId)}-${breakId}`;
}

export function formatRegionName(
  regionLabel: string,
  countryLabel: string | null | undefined,
): string {
  const region = regionLabel.trim();
  const country = countryLabel?.trim() ?? "";
  if (!country) return region;
  const regionLower = region.toLowerCase();
  const countryLower = country.toLowerCase();
  if (regionLower === countryLower) return region;
  if (regionLower.endsWith(`, ${countryLower}`)) return region;
  return `${region}, ${country}`;
}

export function publicCamUrl(
  cameras: Pick<CameraStream, "url">[] | null | undefined,
): string | null {
  for (const camera of cameras ?? []) {
    const url = camera?.url?.trim();
    if (url && /^https?:\/\//i.test(url)) return url;
  }
  return null;
}

/** 8-point compass. Degrees are meteorological (where the swell/wind comes from). */
export function compass8(degrees: number | null | undefined): string | null {
  if (degrees == null || !Number.isFinite(degrees)) return null;
  const normalized = ((degrees % 360) + 360) % 360;
  const index = Math.round(normalized / 45) % 8;
  return COMPASS_8[index];
}

export function verdictFromMeanStars(meanStars: number): PublicVerdict {
  if (meanStars >= VERDICT_GO_MIN_STARS) return "GO";
  if (meanStars >= VERDICT_FAIR_MIN_STARS) return "FAIR";
  return "POOR";
}

export function verdictFromRating(rating: SurfRating): PublicVerdict {
  return verdictFromMeanStars(RATING_CONFIG[rating].stars);
}

/**
 * Slack when the level changes by under 0.15 m over the next hour.
 * That is about the same sensitivity as the in-app tide arrow, which
 * treats a 0.05 m change across a 15-minute sample as a turn.
 */
export const TIDE_SLACK_METERS_PER_HOUR = 0.15;

export function tideTrendFromDelta(
  heightM: number,
  nextHeightM: number | null,
): TideTrendLabel | null {
  if (nextHeightM == null || !Number.isFinite(heightM) || !Number.isFinite(nextHeightM)) {
    return null;
  }
  const delta = nextHeightM - heightM;
  if (delta > TIDE_SLACK_METERS_PER_HOUR) return "rising";
  if (delta < -TIDE_SLACK_METERS_PER_HOUR) return "dropping";
  return "slack";
}

function finite(value: number | null | undefined): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function round1(value: number): number {
  return Math.round(value * 10) / 10;
}

/** Same digits `swell_ft` uses, so a reason cannot round a second time. */
export function formatForecastFeet(feet: number): string {
  return Number.isInteger(feet) ? String(feet) : feet.toFixed(1);
}

/**
 * Swell fields for one hour. Same choice as the dashboard readout
 * (`selectDisplayedSwell`): primary swell, or significant wave height when
 * that swell is missing or is short-period chop under a longer sea.
 */
export function publishedSwell(hour: HourConditions): {
  heightM: number | null;
  periodS: number | null;
  directionDeg: number | null;
} {
  return selectDisplayedSwell({
    swellHeightM: hour.swellHeightM,
    swellPeriodS: hour.swellPeriodS,
    swellDirectionDeg: hour.swellDirectionDeg,
    waveHeightM: hour.waveHeightM,
    wavePeriodS: hour.wavePeriodS,
    waveDirectionDeg: hour.waveDirectionDeg,
  });
}

const EMPTY_FORECAST: PublicForecastFields = {
  swell_ft: null,
  swell_period_s: null,
  swell_direction: null,
  wind_mph: null,
  wind_direction: null,
  gust_mph: null,
  tide_ft: null,
  tide_trend: null,
  water_temp_f: null,
};

export function forecastFieldsFromHour(
  hour: HourConditions | null | undefined,
): PublicForecastFields {
  if (!hour) return { ...EMPTY_FORECAST };
  const swell = publishedSwell(hour);
  return {
    swell_ft: finite(swell.heightM)
      ? round1(convertHeight(swell.heightM, "imperial"))
      : null,
    swell_period_s: finite(swell.periodS) ? Math.round(swell.periodS) : null,
    swell_direction: compass8(swell.directionDeg),
    wind_mph: finite(hour.windSpeedKmh)
      ? Math.round(hour.windSpeedKmh * KMH_TO_MPH)
      : null,
    wind_direction: compass8(hour.windDirectionDeg),
    gust_mph: finite(hour.windGustKmh) ? Math.round(hour.windGustKmh * KMH_TO_MPH) : null,
    tide_ft: finite(hour.tideHeightM)
      ? round1(convertHeight(hour.tideHeightM, "imperial"))
      : null,
    tide_trend: hour.tideTrend,
    water_temp_f: finite(hour.waterTempC) ? Math.round(hour.waterTempC * 1.8 + 32) : null,
  };
}

function angleDiff(a: number, b: number): number {
  let diff = Math.abs(a - b) % 360;
  if (diff > 180) diff = 360 - diff;
  return diff;
}

/**
 * Short wind clause. Offshore / onshore uses the same shore-normal
 * convention as `rateSurfQuality` (direction the coast faces, wind coming
 * from). Under 5 km/h is calm — the rater's glass-off threshold.
 */
export function describeWind(
  windSpeedKmh: number | null,
  windDirectionDeg: number | null,
  shoreNormal: number | null,
  gustKmh: number | null = null,
): string | null {
  if (!finite(windSpeedKmh)) return null;
  if (windSpeedKmh < 5) return "calm wind";
  // Round first so the clause matches the published mph. 10.3 mph
  // becomes 10, and 10 must not fall back under the light threshold
  // because of a second conversion.
  const sustainedMph = Math.round(windSpeedKmh * KMH_TO_MPH);
  const gustMph = finite(gustKmh) ? Math.round(gustKmh * KMH_TO_MPH) : null;
  const light = sustainedMph < 10 && (gustMph == null || gustMph < 15);
  if (!finite(windDirectionDeg) || !finite(shoreNormal)) {
    return light ? "light wind" : "wind";
  }
  const diff = angleDiff(windDirectionDeg, shoreNormal);
  const kind = diff < 60 ? "onshore" : diff > 120 ? "offshore" : "cross-shore";
  return light ? `light ${kind} wind` : `${kind} wind`;
}

export function buildConditionReason(
  hour: HourConditions,
  shoreNormal: number | null,
): string | null {
  // Quote the published forecast fields. A second conversion, or a
  // different hour, is how a reason used to cite 3.4 ft W while
  // forecast.swell_ft was 4.3 NW.
  const forecast = forecastFieldsFromHour(hour);
  const bits: string[] = [];
  if (forecast.swell_ft != null) {
    const feet = formatForecastFeet(forecast.swell_ft);
    bits.push(
      forecast.swell_direction
        ? `${feet} ft ${forecast.swell_direction} swell`
        : `${feet} ft swell`,
    );
  }
  if (forecast.wind_mph != null) {
    const wind = describeWind(
      hour.windSpeedKmh,
      hour.windDirectionDeg,
      shoreNormal,
      hour.windGustKmh,
    );
    if (wind) bits.push(wind);
  }
  if (forecast.tide_trend) bits.push(`${forecast.tide_trend} tide`);
  if (bits.length === 0) return null;
  const sentence = bits.join(", ");
  return `${sentence.charAt(0).toUpperCase()}${sentence.slice(1)}.`;
}

/**
 * A reason disagrees when it prints a swell size, compass direction, or
 * tide trend that is not the spot's forecast. Omitted pieces are allowed.
 * Wind stays qualitative and does not add a second number.
 */
export function reasonDisagreements(
  spot: Pick<PublicSpot, "forecast" | "best_window">,
): string[] {
  const reason = spot.best_window?.reason;
  if (!reason) return [];
  const problems: string[] = [];
  const expectedFeet =
    spot.forecast.swell_ft == null ? null : formatForecastFeet(spot.forecast.swell_ft);
  for (const match of reason.matchAll(/(\d+(?:\.\d+)?) ft\b/g)) {
    if (match[1] !== expectedFeet) {
      problems.push(`feet ${match[1]} != ${expectedFeet ?? "null"}`);
    }
  }
  for (const match of reason.matchAll(/\b(N|NE|E|SE|S|SW|W|NW)\b/g)) {
    if (match[1] !== spot.forecast.swell_direction) {
      problems.push(
        `direction ${match[1]} != ${spot.forecast.swell_direction ?? "null"}`,
      );
    }
  }
  const tide = /\b(rising|dropping|slack) tide\b/.exec(reason);
  if (tide && tide[1] !== spot.forecast.tide_trend) {
    problems.push(`tide ${tide[1]} != ${spot.forecast.tide_trend ?? "null"}`);
  }
  return problems;
}

function breakTypeOf(value: string | undefined): BreakType | null {
  if (value === "beach" || value === "reef" || value === "point") return value;
  return null;
}

function rateHour(
  hour: HourConditions,
  spot: CatalogSpot,
): { rating: SurfRating; stars: number } | null {
  const swell = publishedSwell(hour);
  const offshoreM = finite(hour.waveHeightM) ? hour.waveHeightM : swell.heightM;
  if (!finite(offshoreM)) return null;
  const periodS = finite(hour.wavePeriodS) ? hour.wavePeriodS : swell.periodS;
  const faceM = finite(periodS)
    ? estimateSurfFaceHeight(offshoreM, periodS, spot.breakType ?? "beach")
    : offshoreM;
  const rated = rateSurfQuality({
    waveHeightFt: faceM * 3.28084,
    period: finite(periodS) ? periodS : null,
    windSpeedKmh: hour.windSpeedKmh,
    windGustKmh: hour.windGustKmh,
    windDirectionDeg: hour.windDirectionDeg,
    swellDirectionDeg: swell.directionDeg ?? hour.waveDirectionDeg,
    shoreNormal: spot.shoreNormal,
  });
  return { rating: rated.rating, stars: RATING_CONFIG[rated.rating].stars };
}

function localClockHour(stamp: string): number | null {
  const match = /T(\d{2})/.exec(stamp);
  if (!match) return null;
  const hour = Number(match[1]);
  return Number.isInteger(hour) ? hour : null;
}

function localHm(stamp: string): string | null {
  const match = /T(\d{2}:\d{2})/.exec(stamp);
  return match ? match[1] : null;
}

export function selectBestWindow(
  spot: CatalogSpot,
  conditions: SpotConditions,
  now: Date,
): PublicBestWindow | null {
  const timezone = spot.timezone ?? conditions.timezone;
  if (!timezone) return null;
  const offset = conditions.utcOffsetSeconds;
  const nowMs = now.getTime();
  const candidates: { hour: HourConditions; atMs: number; stars: number }[] = [];

  for (const hour of conditions.hourly) {
    if (!hour.time) continue;
    const atMs = localTimeToUtcMs(hour.time, offset);
    if (!Number.isFinite(atMs)) continue;
    if (atMs + 60 * 60 * 1000 <= nowMs) continue;
    if (atMs > nowMs + BEST_WINDOW_HORIZON_MS) continue;
    const clock = localClockHour(hour.time);
    if (clock == null || clock < DAYLIGHT_START_HOUR || clock > DAYLIGHT_END_HOUR) {
      continue;
    }
    const rated = rateHour(hour, spot);
    if (!rated) continue;
    candidates.push({ hour, atMs, stars: rated.stars });
  }

  let bestStart = -1;
  let bestScore = -1;
  for (let i = 0; i + BEST_WINDOW_HOURS <= candidates.length; i++) {
    let consecutive = true;
    for (let step = 1; step < BEST_WINDOW_HOURS; step++) {
      if (candidates[i + step].atMs - candidates[i + step - 1].atMs !== 3_600_000) {
        consecutive = false;
        break;
      }
    }
    if (!consecutive) continue;
    let score = 0;
    for (let step = 0; step < BEST_WINDOW_HOURS; step++)
      score += candidates[i + step].stars;
    if (score > bestScore) {
      bestScore = score;
      bestStart = i;
    }
  }
  if (bestStart < 0) return null;

  const slice = candidates.slice(bestStart, bestStart + BEST_WINDOW_HOURS);
  const mean = slice.reduce((sum, item) => sum + item.stars, 0) / slice.length;
  // The sentence quotes the forecast sample (conditions at generated_at),
  // not the middle hour of the window. Those hours can differ in size,
  // direction, and tide.
  const reason = buildConditionReason(conditions.current, spot.shoreNormal);
  const start = slice[0].hour.time ? localHm(slice[0].hour.time) : null;
  if (!reason || !start) return null;

  const endMs = slice[slice.length - 1].atMs + 3_600_000;
  const end = new Date(endMs + offset * 1000).toISOString().slice(11, 16);
  return {
    start,
    end,
    timezone,
    verdict: verdictFromMeanStars(mean),
    reason,
  };
}

export function crowdCountsOrNull(
  queryOk: boolean,
  counts: { reports_last_2h: number; thumbs_up: number; thumbs_down: number } | undefined,
): PublicCrowd {
  if (!queryOk) {
    return { reports_last_2h: null, thumbs_up: null, thumbs_down: null };
  }
  return {
    reports_last_2h: counts?.reports_last_2h ?? 0,
    thumbs_up: counts?.thumbs_up ?? 0,
    thumbs_down: counts?.thumbs_down ?? 0,
  };
}

export function buildPublicSpot(
  spot: CatalogSpot,
  conditions: SpotConditions | null,
  crowd: PublicCrowd,
  now: Date,
): PublicSpot {
  return {
    id: spot.id,
    name: spot.name,
    region: spot.region,
    lat: spot.lat,
    lon: spot.lon,
    cam_url: spot.cam_url,
    forecast: forecastFieldsFromHour(conditions?.current),
    best_window: conditions ? selectBestWindow(spot, conditions, now) : null,
    crowd,
  };
}

function countryByRegion(zones: Zone[]): Map<string, string> {
  const map = new Map<string, string>();
  for (const zone of zones) {
    for (const country of zone.countries ?? []) {
      for (const region of country.regions ?? []) {
        map.set(region.id, country.label);
      }
    }
  }
  return map;
}

/** Zone-tree region label. That is the name the nav, title, and spot index show. */
function zoneLabelByRegion(zones: Zone[]): Map<string, string> {
  const map = new Map<string, string>();
  for (const zone of zones) {
    for (const country of zone.countries ?? []) {
      for (const region of country.regions ?? []) {
        if (region.label) map.set(region.id, region.label);
      }
    }
  }
  return map;
}

function isBreak(value: SurfBreak | undefined): value is SurfBreak {
  return Boolean(
    value &&
    typeof value.id === "string" &&
    value.id &&
    typeof value.name === "string" &&
    value.name &&
    typeof value.lat === "number" &&
    Number.isFinite(value.lat) &&
    typeof value.lon === "number" &&
    Number.isFinite(value.lon),
  );
}

/**
 * Every break in the catalog. Ids are assigned in region-id / break-id
 * order so a rare collision gets a stable `-2` suffix regardless of
 * display sort. The HTTP response sorts by name separately.
 */
export function listCatalogSpots(
  regions: Record<string, RegionConfig>,
  zones: Zone[],
): CatalogSpot[] {
  const countries = countryByRegion(zones);
  const zoneLabels = zoneLabelByRegion(zones);
  const drafts: CatalogSpot[] = [];
  const regionIds = Object.keys(regions).sort((a, b) => a.localeCompare(b));
  for (const regionId of regionIds) {
    const region = regions[regionId];
    if (!region) continue;
    const breaks = [...(region.breaks ?? [])].sort((a, b) =>
      String(a?.id ?? "").localeCompare(String(b?.id ?? "")),
    );
    for (const surfBreak of breaks) {
      if (!isBreak(surfBreak)) continue;
      drafts.push({
        id: publicSpotId(regionId, surfBreak.id),
        regionId,
        breakId: surfBreak.id,
        name: surfBreak.name,
        region: formatRegionName(
          zoneLabels.get(regionId) || region.label || regionId,
          countries.get(regionId),
        ),
        lat: surfBreak.lat,
        lon: surfBreak.lon,
        shoreNormal:
          typeof surfBreak.shoreNormal === "number" ? surfBreak.shoreNormal : null,
        breakType: breakTypeOf(surfBreak.breakType),
        timezone:
          typeof region.timezone === "string" && region.timezone ? region.timezone : null,
        cam_url: publicCamUrl(surfBreak.cameras),
      });
    }
  }

  const seen = new Map<string, number>();
  for (const spot of drafts) {
    const count = seen.get(spot.id) ?? 0;
    seen.set(spot.id, count + 1);
    if (count > 0) spot.id = `${spot.id}-${count + 1}`;
  }
  return drafts;
}

export function countCatalogBreaks(regions: Record<string, RegionConfig>): number {
  let count = 0;
  for (const region of Object.values(regions)) {
    for (const surfBreak of region?.breaks ?? []) {
      if (isBreak(surfBreak)) count += 1;
    }
  }
  return count;
}
